import axios from 'axios'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { useOAuthService } from '@/services/OAuthService'
import { getCustomer } from '@/assets/Branding/branding.js'
import { getBaseUrl, getConfig, getBuildings } from '@/controllers/BaseController'
import { getHardwareByType } from '@/controllers/BackboneAPI'
import { BlueIDSecretsPlugin } from '@/services/blueIdSecretsPlugin'

// How often an open of Locks checks the door list against the user's groups. The check
// only writes to BlueID when something changed, so group changes arrive within the hour
// without a logout.
export const SYNC_INTERVAL_MS = 60 * 60 * 1000

/**
 * Checks whether the sync interval has elapsed since the last full backend & BlueID sync
 */
export const shouldPerformDailySync = async () => {
  try {
    const { value } = await Preferences.get({ key: 'blueid_last_daily_sync_time' })
    if (!value) return true
    const lastSync = parseInt(value, 10)
    if (isNaN(lastSync)) return true
    return (Date.now() - lastSync) >= SYNC_INTERVAL_MS
  } catch (e) {
    return true
  }
}

// BlueID returns at most 100 entries per call and Hammerbrooklyn has more; the entries
// past 100 (sorted by name: the TRH-2/TRH-3 staircases) used to go missing from keys.
// ponytail: capped at 1000 entries, raise if a site ever grows beyond that.
export const getAllBlueIDPages = async (method, customer, key) => {
  const items = []
  for (let offset = 0; offset < 1000; offset += 100) {
    const page = ((await BlueIDSecretsPlugin[method]({ customer, offset })) || {})[key] || []
    items.push(...page)
    if (page.length < 100) break
  }
  return items
}
export const getAllBlueIDObjects = customer => getAllBlueIDPages('getLocks', customer, 'objects')
export const getAllBlueIDDevices = customer => getAllBlueIDPages('getDevices', customer, 'devices')

/**
 * Records the timestamp of a successful daily sync
 */
export const recordDailySyncCompleted = async () => {
  try {
    await Preferences.set({
      key: 'blueid_last_daily_sync_time',
      value: String(Date.now()),
    })
  } catch (e) {
    console.warn('[BlueID] Failed to record daily sync time:', e)
  }
}

/**
 * Retrieves persistently cached door objects for instant display without waiting for network
 */
export const getCachedDoors = async () => {
  try {
    const { value } = await Preferences.get({ key: 'blueid_cached_doors' })
    return value ? JSON.parse(value) : []
  } catch (e) {
    return []
  }
}

/**
 * Persistently caches door objects
 */
export const setCachedDoors = async (doors) => {
  try {
    if (Array.isArray(doors)) {
      await Preferences.set({
        key: 'blueid_cached_doors',
        value: JSON.stringify(doors),
      })
    }
  } catch (e) {
    console.warn('[BlueID] Failed to cache doors:', e)
  }
}

// The user's groups as they are now. Read from the backend, not from the login token:
// the app keeps one token for days, so its group list would lag behind user management.
const fetchCurrentGroups = async sub => {
  const config = await getConfig()
  const root = (await getBaseUrl()).replace(/v3\/?$/, '')
  config.url = `${root}backbone/v3/users/${sub}/groups`
  config.method = 'GET'
  const { data } = await axios(config)
  if (!Array.isArray(data)) throw new Error('Unexpected groups response')
  return data
}

const fetchGroupLockIds = async groupId => {
  const config = await getConfig()
  config.url = `${await getBaseUrl()}groups/${groupId}/permissions`
  config.method = 'GET'
  const { data } = await axios(config)
  return Object.keys(data?.permissionsBySecuredObjects?.HARDWARE || {})
}

/**
 * The doors the user may open, exactly as Pinestack grants them: the user's current
 * groups, each group's lock permissions, and each lock's BlueID entry. Names, roles and
 * token claims play no part. Any failure returns no doors, so nothing gets written.
 */
export const getUserEntitledLocks = async ({ profile, customer: passedCustomer } = {}) => {
  try {
    const userProfile = profile || (await useOAuthService().getUserProfile())
    const customer = passedCustomer || (await getCustomer())

    const groups = await fetchCurrentGroups(userProfile.sub)
    const lockIds = new Set((await Promise.all(groups.map(g => fetchGroupLockIds(g.id)))).flat())

    const building = (await getBuildings())?.[0]
    const locks = building ? await getHardwareByType(building.id, 'lock', true) : null
    if (!Array.isArray(locks)) throw new Error('Pinestack locks unavailable')
    // PIA's Pinestack locks carry only the old BlueID id, which the NextGen entry keeps as its
    // legacy id. The NextGen id wins; the old one is used only for locks without it.
    const blueIdObjects = await getAllBlueIDObjects(customer)
    const legacyIdOf = o => o.legacySecureObjectId || o.configuration?.legacySecureObjectId
    const byLegacyId = new Map(blueIdObjects.filter(legacyIdOf).map(o => [legacyIdOf(o), o._id]))
    const objectIds = new Set(
      locks
        .filter(l => lockIds.has(l.id))
        .map(l => l.configs?.['blue-id-v2-object-id'] || byLegacyId.get(l.configs?.['blue-id-object-id']))
        .filter(Boolean)
    )

    const entitledLocks = blueIdObjects.filter(o => objectIds.has(o._id))
    console.log(`[BlueID] Entitled doors: ${entitledLocks.length} from ${groups.length} group(s)`)
    // loaded: every lookup answered, so an empty list really means "no doors".
    return { psGroups: groups, entitledLocks, loaded: true }
  } catch (error) {
    console.error('[BlueID] Error resolving entitled lock objects:', error)
    return { psGroups: [], entitledLocks: [], loaded: false }
  }
}

/**
 * Checks if the user's active BlueID Cloud identity is missing any entitled lock objects or holds revoked locks.
 * Reconciles lock objects directly to the identity on BlueID Cloud.
 */
export const syncIdentityLocksIfMissing = async ({ customer: passedCustomer, identityId, profile } = {}) => {
  try {
    if (!Capacitor.isNativePlatform()) {
      return { updated: false, reason: 'web-platform' }
    }

    const customer = passedCustomer || (await getCustomer())
    const userProfile = profile || (await useOAuthService().getUserProfile())
    const { entitledLocks, loaded } = await getUserEntitledLocks({ profile: userProfile, customer, force: true })

    // Only a complete answer may change the key: after an error the current doors stay.
    if (!loaded) {
      return { updated: false, reason: 'entitlements-unavailable' }
    }

    // Only this phone's own identity, read by id. The identity list stops at 100 entries,
    // and matching by email also caught identities other people (BlueID, facility
    // management) created for the same person.
    const targetIdentity = identityId
      ? await BlueIDSecretsPlugin.getIdentity({ customer, identityId }).catch(err => {
          console.warn('[BlueID] Could not read own identity:', err)
          return null
        })
      : null

    if (!targetIdentity?._id) {
      console.warn('[BlueID] Target identity not found on BlueID Cloud for sync.')
      return { updated: false, reason: 'identity-not-found' }
    }

    const isMasterIdentity = String(targetIdentity.role || '').toLowerCase() === 'master'
    // Master keys come from the backend (e.g. General Key); the app never promotes one.

    if (isMasterIdentity) {
      console.log('[BlueID] Master identity detected on BlueID Cloud. Access to all doors is active automatically.')
      return { updated: false, identity: targetIdentity, isMaster: true }
    }

    // PIA registers keys in the app, not through the backend, so admins (roles from the login
    // token) need a master key. BlueID ignores role changes on an existing key, so a regular
    // key of an admin is replaced instead. On HBC the backend decides.
    const ADMIN_ROLES = /^(globaladmin|superadmin|supermember|master)$/i
    if (customer === 'pia' && (userProfile.realm_access?.roles || []).some(r => ADMIN_ROLES.test(r))) {
      return { updated: false, replaceKey: true, reason: 'needs-master-key' }
    }

    const currentObjectIds = new Set(
      (targetIdentity.objects || []).map(o => (typeof o === 'string' ? o : o._id || o.id || '').toLowerCase())
    )
    const entitledObjectIds = new Set(
      entitledLocks.map(l => (l._id || l.id || '').toLowerCase())
    )

    const missingLocks = entitledLocks.filter(
      lock => !currentObjectIds.has((lock._id || lock.id || '').toLowerCase())
    )
    const revokedObjectIds = [...currentObjectIds].filter(id => !entitledObjectIds.has(id))

    if (!missingLocks.length && !revokedObjectIds.length) {
      console.log('[BlueID] Identity lock objects are already in sync with Pinestack entitlements.')
      return { updated: false, identity: targetIdentity }
    }

    console.log(`[BlueID] Entitlement change detected (+${missingLocks.length} added, -${revokedObjectIds.length} revoked). Syncing BlueID Cloud identity...`)

    const updatedObjects = entitledLocks.map(l => {
      const objId = typeof l === 'string' ? l : l._id || l.id || l.objectId
      const objName = (typeof l === 'object' && l.name) ? l.name : 'Lock'
      return {
        _id: String(objId),
        name: String(objName),
        accessType: 'default',
      }
    })

    const nowDate = new Date()
    const validTo = new Date(nowDate)
    validTo.setFullYear(validTo.getFullYear() + 3)

    const targetRole = targetIdentity.role || 'regular'
    const isMasterRole = targetRole === 'master'

    const validFromStr = (targetIdentity.validFrom && String(targetIdentity.validFrom).length > 5)
      ? targetIdentity.validFrom
      : nowDate.toISOString()
    const validToStr = (targetIdentity.validTo && String(targetIdentity.validTo).length > 5)
      ? targetIdentity.validTo
      : validTo.toISOString()

    const userEmail = targetIdentity.email || userProfile.email || 'mobile@pinestack.com'
    const userPhone = targetIdentity.mobilePhone || '+490000000000'
    const userRefresh = typeof targetIdentity.refresh === 'number' ? targetIdentity.refresh : 30

    const payload = {
      name: targetIdentity.name || userProfile.name || userProfile.email || 'User',
      description: targetIdentity.description || userProfile.email || '',
      email: userEmail,
      mobilePhone: userPhone,
      role: targetRole,
      validFrom: validFromStr,
      validTo: validToStr,
      refresh: userRefresh,
      schedules: targetIdentity.schedules || [],
      templates: targetIdentity.templates || [],
    }

    if (!isMasterRole) {
      payload.objects = updatedObjects
      // Doors go on as objects; groups someone set on the identity in BlueID stay.
      payload.groups = targetIdentity.groups || []
    }

    await BlueIDSecretsPlugin.updateIdentity({
      customer,
      identityId: targetIdentity._id,
      payload,
    })

    console.log(`✅ Identity successfully reconciled on BlueID Cloud (+${missingLocks.length}, -${revokedObjectIds.length})`)
    return {
      updated: true,
      addedLocks: missingLocks,
      revokedObjectIds,
      identity: { ...targetIdentity, objects: updatedObjects },
    }
  } catch (error) {
    console.error('❌ Failed to sync missing identity lock objects on BlueID Cloud:', error)
    return { updated: false, error: error.message }
  }
}
